/**
 * Orquestación de la búsqueda global para la UI (modal Cmd/Ctrl+K y biblioteca).
 *
 * Envuelve el store de búsqueda con debounce del input y agrupación de
 * resultados por libro (para renderizar secciones en el modal).
 */

import { computed, ref, watch } from 'vue'
import { useSearchStore } from '@/stores/search'
import { useLibraryStore } from '@/stores/library'
import type { SearchHit } from '@/types/search'

export interface GlobalSearchGroup {
  bookId: string
  title: string
  author: string
  hits: SearchHit[]
}

export function useGlobalSearch(debounceMs = 250) {
  const searchStore = useSearchStore()
  const library = useLibraryStore()

  /** Texto del input (bindeado al campo). */
  const query = ref('')
  /** Texto efectivo (tras debounce). */
  const queryDebounced = ref('')
  const hits = ref<SearchHit[]>([])
  const searching = ref(false)

  /** Resultados agrupados por libro (ordenado por mejor score del grupo). */
  const groups = computed<GlobalSearchGroup[]>(() => {
    const byBook = new Map<string, SearchHit[]>()
    for (const hit of hits.value) {
      const arr = byBook.get(hit.bookId) ?? []
      arr.push(hit)
      byBook.set(hit.bookId, arr)
    }
    const bookMeta = new Map(library.books.map((b) => [b.id, b]))
    const out: GlobalSearchGroup[] = []
    for (const [bookId, bookHits] of byBook) {
      const book = bookMeta.get(bookId)
      out.push({
        bookId,
        title: book?.title ?? 'Libro',
        author: book?.author ?? '',
        hits: bookHits,
      })
    }
    // Ordenar grupos por el score máximo de sus hits.
    out.sort((a, b) => Math.max(...b.hits.map((h) => h.score)) - Math.max(...a.hits.map((h) => h.score)))
    return out
  })

  // Debounce del input.
  let timer: ReturnType<typeof setTimeout> | null = null
  watch(query, (value) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(async () => {
      queryDebounced.value = value.trim()
      if (queryDebounced.value.length < 2) {
        hits.value = []
        return
      }
      searching.value = true
      try {
        hits.value = await searchStore.search(queryDebounced.value)
      } finally {
        searching.value = false
      }
    }, debounceMs)
  })

  function clear() {
    query.value = ''
    queryDebounced.value = ''
    hits.value = []
  }

  return {
    query,
    queryDebounced,
    hits,
    groups,
    searching,
    ready: computed(() => searchStore.ready),
    /** Construye el índice FlexSearch. Idempotente; llamar al abrir el modal. */
    init: () => searchStore.init(),
    clear,
  }
}
